/**
 * Centralized Sample Filter Constants
 *
 * Canonical lists of statuses and analysis types used by the samples
 * list filter popover. Sourced from the backend OpenAPI spec
 * (`SampleStatus` enum and the analysis-type keys) so that web and
 * mobile share a single shape.
 */

import type { SampleStatus } from "@/types/central";

/**
 * Upper bound for per-sample analyses fetches in dropdown/picker contexts.
 * Intentionally generous so the UI never silently truncates options.
 */
export const MAX_ANALYSES_PER_SAMPLE = 200;

/** OpenAPI SampleBulkDetailsUpdate / bulk delete: max sample UUIDs per request. */
export const MAX_BULK_SAMPLE_IDS = 100;

export interface SampleFilterOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

/**
 * Sample status options for the samples list filter popover.
 * Order mirrors the backend processing pipeline.
 */
export const SAMPLE_STATUS_FILTER_OPTIONS: ReadonlyArray<
  SampleFilterOption<SampleStatus>
> = [
  { value: "pending", label: "Pending" },
  { value: "scheduled", label: "Scheduled" },
  { value: "processing", label: "Processing" },
  { value: "inference_complete", label: "Inference Complete" },
  { value: "post_processing", label: "Post Processing" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "archived", label: "Archived" },
  { value: "processed", label: "Processed" },
] as const;

/**
 * Analysis status filter options for the samples list.
 * Order follows the backend processing pipeline.
 * Only shown when an analysis type is selected.
 */
export const ANALYSIS_STATUS_FILTER_OPTIONS: ReadonlyArray<SampleFilterOption> =
  [
    { value: "pending", label: "Pending" },
    { value: "queued", label: "Queued" },
    { value: "processing", label: "Processing" },
    { value: "processed", label: "Processed" },
    { value: "sign_off_requested", label: "Sign Off Requested" },
    { value: "completed", label: "Completed" },
    { value: "archived", label: "Archived" },
    { value: "cancelled", label: "Cancelled" },
    { value: "failed", label: "Failed" },
  ] as const;
